import { useEffect, useState } from "react";
import api, { API, apiError, getToken } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useMeta } from "@/context/MetaContext";
import { PageHeader, Loading, EmptyState } from "@/components/common/PageHeader";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { FileDown, FileSpreadsheet, FileText, FileBarChart } from "lucide-react";

const REPORTS = [
  { value: "sales", label: "Revenue / Sales" },
  { value: "expenses", label: "Expenses" },
  { value: "inventory", label: "Inventory Valuation" },
  { value: "stock_movement", label: "Stock Movement" },
  { value: "wastage", label: "Wastage" },
  { value: "purchases", label: "Purchases" },
  { value: "payments", label: "Supplier Payments" },
  { value: "cash", label: "Cash Reconciliation" },
  { value: "audit", label: "Audit Trail" },
];

export default function Reports() {
  const { can, user } = useAuth();
  const { outletOptions } = useMeta();
  const isSuper = user?.role === "super_admin" || user?.role === "finance";
  const [type, setType] = useState("sales");
  const [outlet, setOutlet] = useState("");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [data, setData] = useState({ columns: [], rows: [] });
  const [loading, setLoading] = useState(false);

  const params = () => ({ type, outlet_id: outlet || undefined, from_date: from || undefined, to_date: to || undefined });

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try { const { data } = await api.get("/reports/data", { params: params() }); setData(data); }
      catch (e) { toast.error(apiError(e)); }
      finally { setLoading(false); }
    };
    load();
    // eslint-disable-next-line
  }, [type, outlet, from, to]);

  const download = async (format) => {
    try {
      const qs = new URLSearchParams({ type, format, ...(outlet ? { outlet_id: outlet } : {}), ...(from ? { from_date: from } : {}), ...(to ? { to_date: to } : {}) });
      const res = await fetch(`${API}/reports/export?${qs}`, { headers: { Authorization: `Bearer ${getToken()}` }, credentials: "include" });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      if (format === "pdf") {
        const url = URL.createObjectURL(blob);
        window.open(url, "_blank");
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url; a.download = `hunter_${type}.${format === "excel" ? "csv" : format}`; a.click();
        URL.revokeObjectURL(url);
      }
      toast.success(`${format.toUpperCase()} exported`);
    } catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div className="space-y-6">
      <PageHeader title="Reports" subtitle="Filterable, exportable reports across every module.">
        {can("reports:export") && (
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => download("csv")} data-testid="export-csv"><FileDown className="mr-1.5 h-4 w-4" />CSV</Button>
            <Button variant="outline" size="sm" onClick={() => download("excel")} data-testid="export-excel"><FileSpreadsheet className="mr-1.5 h-4 w-4" />Excel</Button>
            <Button variant="outline" size="sm" onClick={() => download("pdf")} data-testid="export-pdf"><FileText className="mr-1.5 h-4 w-4" />PDF</Button>
          </div>
        )}
      </PageHeader>

      <div className="flex flex-wrap items-end gap-3 rounded-sm border border-border bg-card p-4">
        <div className="space-y-1.5"><span className="eyebrow">Report</span>
          <Select value={type} onValueChange={setType}><SelectTrigger className="w-56" data-testid="report-type"><SelectValue /></SelectTrigger><SelectContent>{REPORTS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}</SelectContent></Select>
        </div>
        {isSuper && (
          <div className="space-y-1.5"><span className="eyebrow">Outlet</span>
            <Select value={outlet || "all"} onValueChange={(v) => setOutlet(v === "all" ? "" : v)}><SelectTrigger className="w-44" data-testid="report-outlet"><SelectValue /></SelectTrigger><SelectContent><SelectItem value="all">All outlets</SelectItem>{outletOptions.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}</SelectContent></Select>
          </div>
        )}
        <div className="space-y-1.5"><span className="eyebrow">From</span><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} className="w-40" data-testid="report-from" /></div>
        <div className="space-y-1.5"><span className="eyebrow">To</span><Input type="date" value={to} onChange={(e) => setTo(e.target.value)} className="w-40" data-testid="report-to" /></div>
      </div>

      <div className="overflow-x-auto rounded-sm border border-border bg-card">
        {loading ? <Loading /> : data.rows.length === 0 ? <EmptyState icon={FileBarChart} title="No data" hint="Adjust filters or date range." /> : (
          <Table>
            <TableHeader><TableRow className="bg-secondary/60">{data.columns.map((c) => <TableHead key={c} className="whitespace-nowrap text-xs uppercase tracking-wider text-muted-foreground">{c.replace(/_/g, " ")}</TableHead>)}</TableRow></TableHeader>
            <TableBody>
              {data.rows.slice(0, 200).map((r, i) => (
                <TableRow key={r.id || i} className="border-border"><>{data.columns.map((c) => <TableCell key={c} className="whitespace-nowrap py-2 text-sm">{String(r[c] ?? "—").slice(0, 40)}</TableCell>)}</></TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
      {data.rows.length > 200 && <p className="text-center text-xs text-muted-foreground">Showing first 200 of {data.rows.length}. Export for full data.</p>}
    </div>
  );
}
